import {
  type ApiUnauthenticatedError,
  type AccountsRepository,
  type AuthStrategy,
  type CarsRepository,
  type ContactsRepository,
  type CrossLedgerDebtsRepository,
  type CrossLedgerLinksRepository,
  type CurrenciesRepository,
  anonymousAuth,
  createAccountsRepository,
  createCarsRepository,
  createContactsRepository,
  createCrossLedgerDebtsRepository,
  createCrossLedgerLinksRepository,
  createCurrenciesRepository,
  createEntriesRepository,
  createExportsRepository,
  createForecastRepository,
  createIdentityRepository,
  createInvitationsRepository,
  createImportsRepository,
  createLoansRepository,
  createMembersRepository,
  createMerchantsRepository,
  createNotificationsRepository,
  createPrepaidRecognitionsRepository,
  createReceiptScansRepository,
  createReconciliationsRepository,
  createRelationshipsRepository,
  createReportingRepository,
  createSessionRepository,
  createSnapshotsRepository,
  createTalerClient,
  createTagsRepository,
  createTargetsRepository,
  createTransfersRepository,
  type EntriesRepository,
  type ExportsRepository,
  type ForecastRepository,
  type IdentityRepository,
  type ImportsRepository,
  type InvitationsRepository,
  type LoansRepository,
  type MembersRepository,
  type MerchantsRepository,
  type NotificationsRepository,
  type PrepaidRecognitionsRepository,
  type ReceiptScansRepository,
  type ReconciliationsRepository,
  type RelationshipsRepository,
  type ReportingRepository,
  type SessionRepository,
  type SnapshotsRepository,
  type TagsRepository,
  type TargetsRepository,
  type TransfersRepository,
} from '@taler/api-client'
import type { InjectionKey } from 'vue'
import { inject } from 'vue'

export interface Api {
  readonly accounts: AccountsRepository
  readonly cars: CarsRepository
  readonly contacts: ContactsRepository
  readonly crossLedgerDebts: CrossLedgerDebtsRepository
  readonly crossLedgerLinks: CrossLedgerLinksRepository
  readonly currencies: CurrenciesRepository
  readonly entries: EntriesRepository
  readonly imports: ImportsRepository
  readonly loans: LoansRepository
  readonly prepaidRecognitions: PrepaidRecognitionsRepository
  readonly receiptScans: ReceiptScansRepository
  readonly exports: ExportsRepository
  readonly reporting: ReportingRepository
  readonly session: SessionRepository
  readonly identity: IdentityRepository
  readonly members: MembersRepository
  readonly merchants: MerchantsRepository
  readonly notifications: NotificationsRepository
  readonly invitations: InvitationsRepository
  readonly transfers: TransfersRepository
  readonly relationships: RelationshipsRepository
  readonly snapshots: SnapshotsRepository
  readonly reconciliations: ReconciliationsRepository
  readonly targets: TargetsRepository
  readonly tags: TagsRepository
  readonly forecast: ForecastRepository
}

export const API_KEY: InjectionKey<Api> = Symbol('taler.api')

export interface CreateApiOptions {
  readonly baseUrl: string
  readonly auth?: AuthStrategy
  readonly onSignInRequired: (error: ApiUnauthenticatedError) => void
}

/** The composition root. Repositories are constructed once and injected; nothing constructs its own. */
export function createApi(options: CreateApiOptions): Api {
  const client = createTalerClient({
    baseUrl: options.baseUrl,
    auth: options.auth ?? anonymousAuth,
    onSignInRequired: options.onSignInRequired,
  })
  return {
    accounts: createAccountsRepository(client),
    cars: createCarsRepository(client),
    contacts: createContactsRepository(client),
    crossLedgerDebts: createCrossLedgerDebtsRepository(client),
    crossLedgerLinks: createCrossLedgerLinksRepository(client),
    currencies: createCurrenciesRepository(client),
    entries: createEntriesRepository(client),
    imports: createImportsRepository(client),
    loans: createLoansRepository(client),
    prepaidRecognitions: createPrepaidRecognitionsRepository(client),
    receiptScans: createReceiptScansRepository(client),
    exports: createExportsRepository(client),
    reporting: createReportingRepository(client),
    session: createSessionRepository(client),
    identity: createIdentityRepository(client),
    members: createMembersRepository(client),
    merchants: createMerchantsRepository(client),
    notifications: createNotificationsRepository(client),
    invitations: createInvitationsRepository(client),
    transfers: createTransfersRepository(client),
    relationships: createRelationshipsRepository(client),
    snapshots: createSnapshotsRepository(client),
    reconciliations: createReconciliationsRepository(client),
    targets: createTargetsRepository(client),
    tags: createTagsRepository(client),
    forecast: createForecastRepository(client),
  }
}

export function useApi(): Api {
  const api = inject(API_KEY)
  if (api === undefined) throw new Error('No API provided. Install the app plugin before using a repository.')
  return api
}
